/**
 * La relecture du quiz de prérequis : la correction que l'élève relit, question par question — ce
 * qu'il a répondu, la bonne réponse, juste ou faux —, et la machine d'états de la feuille qui la
 * montre.
 *
 * L'ancien code (`QuizCorrectionReview` → `PrerequisiteQuizDisplay`, `QuizReviewModal`) la montre à
 * deux endroits :
 *  - **à la fin du quiz**, sous le résultat : la réponse de la tentative la porte déjà
 *    (`POST …/quiz/attempt` → `quiz`, un `QuizResource` qui dit `correct_answer` et
 *    `latest_attempt`) ; à défaut, elle est relue par `GET activities/{id}/quiz` ;
 *  - **« Revoir le quiz »**, dans l'en-tête de l'activité, une fois la tentative faite : relue par
 *    `GET activities/{id}/quiz`, une fois par activité (une correction ne change plus).
 *
 * Le serveur ne révèle `correct_answer` à l'élève qu'une fois sa seule tentative faite : une
 * relecture sans tentative n'a rien à montrer (« Tu n'as pas encore fait ce quiz », comme avant).
 *
 * Pur : ni appel, ni minuteur. Le crochet (`useQuizReview`) branche les appels et la télémétrie.
 */
import { shownChoicesOf } from "../quizChoices"

/** Une question du quiz, telle que la sert `QuizResource` une fois la tentative faite. */
export type ApiQuizReviewQuestion = {
    id?: number | null
    question_text?: string | null
    /** La question avec ses formules en LaTeX ; absente quand elle n'en a pas. */
    display_question_text?: string | null
    options?: unknown[] | null
    /** Les mêmes réponses, rang pour rang, avec leurs formules ; absentes sans formule. */
    display_options?: unknown[] | null
    correct_answer?: unknown
}

/** Une réponse de la tentative (`QuizAttempt::answerEntry`). */
export type ApiQuizAttemptAnswer = {
    quiz_question_id?: number | null
    answer?: unknown
    is_correct?: unknown
}

/** `QuizResource.latest_attempt`. */
export type ApiQuizLatestAttempt = {
    score?: number | null
    total?: number | null
    passed?: unknown
    answers?: ApiQuizAttemptAnswer[] | null
}

/** Ce que la relecture lit d'un `QuizResource`. */
export type ApiQuizForReview = {
    questions?: ApiQuizReviewQuestion[] | null
    latest_attempt?: ApiQuizLatestAttempt | null
}

/** Ce qu'a donné une question : juste, faux, ou rien répondu (l'ancien « Pas de réponse »). */
export type QuizReviewResult = "right" | "wrong" | "unanswered"

export type QuizReviewQuestion = {
    id: number
    text: string
    /** En texte simple : c'est lui que l'élève a répondu et que le serveur juge. */
    options: string[]
    /** Les mêmes, rang pour rang, telles qu'elles s'affichent ; absentes sans formule. */
    displayOptions?: string[]
    /** Ce que l'élève a répondu ; `null` s'il n'a rien répondu. */
    answer: string | null
    /** La bonne réponse ; `null` si le serveur ne l'a pas servie. */
    correctAnswer: string | null
    result: QuizReviewResult
}

export type QuizReview = {
    score: number
    total: number
    /** Arrondi, comme l'ancien anneau. */
    percentage: number
    passed: boolean
    questions: QuizReviewQuestion[]
}

const asText = (value: unknown): string | null => {
    if (value == null) return null
    const text = String(value)
    return text === "" ? null : text
}

/**
 * Un `QuizResource` en relecture. `null` sans tentative : il n'y a pas de correction à montrer.
 *
 * Juste ou faux : le serveur le dit (`is_correct`) ; à défaut, la réponse comparée à la bonne. Une
 * question sans réponse se lit « Pas de réponse » et ne compte pas juste, comme avant.
 */
export function toQuizReview(quiz: ApiQuizForReview | null | undefined): QuizReview | null {
    const attempt = quiz?.latest_attempt
    if (attempt == null) return null
    const answers = Array.isArray(attempt.answers) ? attempt.answers : []
    const questions = (Array.isArray(quiz?.questions) ? (quiz?.questions as ApiQuizReviewQuestion[]) : [])
        .filter((question): question is ApiQuizReviewQuestion & { id: number } => question?.id != null)
        .map((question): QuizReviewQuestion => {
            const given = answers.find((entry) => entry?.quiz_question_id === question.id)
            const answer = asText(given?.answer)
            const correctAnswer = asText(question.correct_answer)
            const right =
                typeof given?.is_correct === "boolean"
                    ? given.is_correct
                    : answer != null && correctAnswer != null && answer === correctAnswer
            const options = (Array.isArray(question.options) ? question.options : []).map((option) =>
                String(option ?? "")
            )
            const shown = shownChoicesOf(options, question.display_options)
            return {
                id: question.id,
                text: question.display_question_text || question.question_text || "",
                options,
                ...(shown !== options ? { displayOptions: shown } : {}),
                answer,
                correctAnswer,
                result: answer == null ? "unanswered" : right ? "right" : "wrong",
            }
        })
    const total = attempt.total ?? questions.length
    const score = attempt.score ?? questions.filter((question) => question.result === "right").length
    return {
        score,
        total,
        percentage: total > 0 ? Math.round((score / total) * 100) : 0,
        passed: attempt.passed === true || attempt.passed === 1,
        questions,
    }
}

/** Ce que la règle de « Revoir le quiz » lit d'une activité. */
export type QuizReviewActivity = { has_prerequisite_quiz?: unknown; quiz_status?: unknown }

/**
 * « Revoir le quiz » s'offre quand l'élève a fait sa tentative : l'activité le dit (`quiz_status`
 * `passed` ou `failed`, la règle de l'ancien en-tête), ou la tentative vient d'être faite ici (la todo
 * n'est pas encore relue, ou l'activité poussée en live n'a pas bougé).
 */
export function canReviewQuiz(activity: QuizReviewActivity | null | undefined, attemptedHere: boolean): boolean {
    if (attemptedHere) return true
    if (!activity?.has_prerequisite_quiz) return false
    return activity.quiz_status === "passed" || activity.quiz_status === "failed"
}

// ── La feuille de la relecture ─────────────────────────────────────────────────────────────────

/**
 * Qui l'a ouverte : la fin du quiz (`attempt`, l'activité attend qu'elle se ferme, comme l'ancien
 * voile), ou « Revoir le quiz » (`request`, par-dessus l'activité, qui continue).
 */
export type QuizReviewOrigin = "attempt" | "request"

/**
 * `idle` : rien de chargé ; `loading` : relue par le serveur ; `ready` : la correction ; `empty` :
 * le serveur n'a pas de tentative ; `error` : la relecture a échoué, l'élève réessaie.
 */
export type QuizReviewStatus = "idle" | "loading" | "ready" | "empty" | "error"

export type QuizReviewState = {
    activityId: number | null
    /** La feuille ouverte, et par qui. `null` : fermée. */
    open: QuizReviewOrigin | null
    status: QuizReviewStatus
    /** Gardée une fois lue : une correction ne change plus, la rouvrir ne coûte rien. */
    review: QuizReview | null
}

export type QuizReviewEvent =
    | { type: "activity"; activityId: number | null }
    /** La tentative est faite : sa réponse porte (ou non) la correction. */
    | { type: "attempted"; quiz: ApiQuizForReview | null }
    /** « Revoir le quiz ». */
    | { type: "requested" }
    | { type: "loaded"; quiz: ApiQuizForReview | null }
    | { type: "loadFailed" }
    | { type: "retry" }
    | { type: "closed" }

export function initialQuizReviewState(activityId: number | null): QuizReviewState {
    return { activityId, open: null, status: "idle", review: null }
}

const settled = (status: QuizReviewStatus) => status === "ready" || status === "empty"

export function quizReviewReducer(state: QuizReviewState, event: QuizReviewEvent): QuizReviewState {
    switch (event.type) {
        case "activity":
            return event.activityId === state.activityId ? state : initialQuizReviewState(event.activityId)
        case "attempted": {
            const review = toQuizReview(event.quiz)
            // La réponse n'a pas porté la correction : on la relit (le repli de l'ancien code).
            if (review == null) return { ...state, open: "attempt", status: "loading", review: null }
            return { ...state, open: "attempt", status: "ready", review }
        }
        case "requested":
            if (state.open != null) return state
            return { ...state, open: "request", status: settled(state.status) ? state.status : "loading" }
        case "loaded": {
            if (state.status !== "loading") return state
            const review = toQuizReview(event.quiz)
            return { ...state, status: review == null ? "empty" : "ready", review }
        }
        case "loadFailed":
            return state.status === "loading" ? { ...state, status: "error" } : state
        case "retry":
            return state.status === "error" && state.open != null ? { ...state, status: "loading" } : state
        case "closed":
            if (state.open == null) return state
            // Une relecture en échec se retente à la prochaine ouverture, comme avant ; une correction lue reste.
            return { ...state, open: null, status: settled(state.status) ? state.status : "idle" }
        default:
            return state
    }
}

/** La relecture ouverte à la fin du quiz retient l'activité, comme le quiz : le chrono attend. */
export function quizReviewBlocks(state: QuizReviewState): boolean {
    return state.open === "attempt"
}
